/**
 * Pure display logic shared by StepRow and the banners, extracted so the rendering rules
 * (org-onboarding-hub tasks 5.5/5.6/6.1/7.2) are testable with `node --test` — this repo
 * has no component-test harness, so anything worth asserting lives in pure modules.
 */

// Steps that stopped being outstanding work render quieter, without an action button.
export const SETTLED_STATES = new Set(['done', 'not_applicable'])

export function deepLinkWithReturn(step) {
    return `${step.deep_link}?from=onboarding&step=${encodeURIComponent(step.step_id)}`
}

// Whether an admin page reached with these query params was entered from the hub —
// the SetupReturnBanner renders only then (spec: "Reaching the same page directly shows
// no banner").
export function isReturningFromSetup(searchParams) {
    return searchParams.get('from') === 'onboarding' && Boolean(searchParams.get('step'))
}

// Whether the acting-as-partner banner should show: the stored context must describe the
// CURRENT active org, for a partner-staff viewer — a stale context or a manual org
// switch must show nothing rather than mislabel whose data is on screen.
export function isActingInOrg(acting, activeOrgId, isPartnerStaff) {
    return Boolean(acting && isPartnerStaff && activeOrgId && acting.actingOrgId === activeOrgId)
}

const EXPLICIT_VERBS = {
    not_applicable: 'marked not applicable',
    skipped: 'skipped',
    waiting: 'marked as waiting',
    acknowledged: 'confirmed'
}

/**
 * "marked not applicable by Chain Partner (your partner) — “No gas supply”", or null when
 * there is nothing attributable. Partner attribution must always outrank the individual
 * actor (task 5.6: the client sees WHICH PARTNER acted, not which of its staff).
 */
export function attributionLine(explicit) {
    if (!explicit) {
        return null
    }
    const actor = explicit.acting_partner
        ? `${explicit.acting_partner.name} (your partner)`
        : (explicit.acted_by?.name ?? null)
    if (!actor) {
        return null
    }
    const verb = EXPLICIT_VERBS[explicit.state]
    if (!verb) {
        return null
    }
    const reason = explicit.reason ? `: “${explicit.reason}”` : ''
    return `${verb} by ${actor}${reason}`
}

// Copy for a step the viewer cannot perform (task 5.5): name the role, never a dead link.
export function delegatedLine(requiredRole) {
    return requiredRole === 'site_admin'
        ? 'Only a site administrator can complete this: ask them to finish it.'
        : 'Only an administrator can complete this: ask one to finish it.'
}

// Whether the sidebar setup entry (and dashboard nudge) should appear at all
// (task 6.3 / spec "Onboarding progress is visible without visiting the hub").
export function showSetupNudge(activeOrg, userIsSetupRole) {
    return Boolean(activeOrg?.id) && !activeOrg?.setup_completed_at && userIsSetupRole
}
